// ./src/components/features/Connectors/ConfigEditors/DiscordConnectorConfigEditor.tsx
import React, { useState, useEffect, useCallback } from "react";
import {
  Stack,
  TextInput,
  Text,
  Alert,
  Code,
  Switch,
  Group,
  Box,
  List,
} from "@mantine/core";
import { IconInfoCircle, IconBrandDiscord } from "@tabler/icons-react";
import { stringifyObject, parseObject } from "@/utils";
import { DiscordConnectorConfig } from "@/types";

export interface DiscordConnectorConfigEditorProps {
  /** JSON string of connector configuration */
  value: string;

  /** Callback when configuration changes */
  onChange: (value: string) => void;

  /** Whether the editor is disabled */
  disabled?: boolean;
}

// Default empty configuration
const DEFAULT_CONFIG: DiscordConnectorConfig = {
  webhookUrl: "",
  showMetadata: true,
  showFiles: true,
  showViewButton: true,
  headerPattern: "📬 New submission: {{formName}}",
};

/**
 * Parse and validate Discord connector configuration
 */
const parseDiscordConfig = (jsonString: string): DiscordConnectorConfig => {
  const parsed = parseObject(jsonString, DEFAULT_CONFIG);
  return parsed;
};

export const DiscordConnectorConfigEditor: React.FC<
  DiscordConnectorConfigEditorProps
> = ({ value, onChange, disabled = false }) => {
  // Parse incoming value to config state
  const [config, setConfig] = useState<DiscordConnectorConfig>(() =>
    parseDiscordConfig(value)
  );

  // Sync config when external value changes
  useEffect(() => {
    const parsed = parseDiscordConfig(value);
    setConfig(parsed);
  }, [value]);

  // Update parent when config changes
  const updateConfig = useCallback(
    (newConfig: DiscordConnectorConfig) => {
      setConfig(newConfig);
      onChange(stringifyObject(newConfig, DEFAULT_CONFIG));
    },
    [onChange]
  );

  // Handle field changes
  const handleWebhookUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateConfig({ ...config, webhookUrl: e.target.value });
  };

  const handleHeaderPatternChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    updateConfig({ ...config, headerPattern: e.target.value });
  };

  const handleShowMetadataChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateConfig({ ...config, showMetadata: e.currentTarget.checked });
  };

  const handleShowFilesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateConfig({ ...config, showFiles: e.currentTarget.checked });
  };

  const handleShowViewButtonChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    updateConfig({ ...config, showViewButton: e.currentTarget.checked });
  };

  // Validate webhook URL format
  const webhookUrlError = (() => {
    if (!config.webhookUrl) return undefined;
    if (
      !config.webhookUrl.startsWith("https://discord.com/api/webhooks/") &&
      !config.webhookUrl.startsWith("https://discordapp.com/api/webhooks/")
    ) {
      return "Must be a valid Discord webhook URL";
    }
    return undefined;
  })();

  return (
    <Stack gap="md">
      {/* Setup Instructions */}
      <Alert
        icon={<IconBrandDiscord size={16} />}
        color="indigo"
        variant="light"
      >
        <Text size="sm" fw={500} mb="xs">
          Setup Instructions:
        </Text>
        <List size="sm" spacing={4}>
          <List.Item>Open your Discord server</List.Item>
          <List.Item>
            Go to <strong>Server Settings</strong> →{" "}
            <strong>Integrations</strong>
          </List.Item>
          <List.Item>
            Click <strong>Webhooks</strong> → <strong>New Webhook</strong>
          </List.Item>
          <List.Item>Choose a channel and optionally rename it</List.Item>
          <List.Item>
            Click <strong>Copy Webhook URL</strong>
          </List.Item>
        </List>
      </Alert>

      {/* Webhook URL Field */}
      <TextInput
        label="Webhook URL"
        placeholder="https://discord.com/api/webhooks/..."
        description="Discord webhook URL"
        value={config.webhookUrl}
        onChange={handleWebhookUrlChange}
        disabled={disabled}
        required
        size="md"
        type="password"
        autoComplete="off"
        error={webhookUrlError}
      />

      {/* Header Pattern Field */}
      <TextInput
        label="Header Pattern"
        placeholder="📬 New submission: {{formName}}"
        description="Customize the embed title"
        value={config.headerPattern || "📬 New submission: {{formName}}"}
        onChange={handleHeaderPatternChange}
        disabled={disabled}
        size="md"
      />

      {/* Toggle Options */}
      <Box>
        <Text size="sm" fw={500} mb="xs">
          Message Options
        </Text>
        <Stack gap="sm">
          <Group justify="space-between">
            <div>
              <Text size="sm">Show Metadata</Text>
              <Text size="xs" c="dimmed">
                Display submission ID and timestamp in footer
              </Text>
            </div>
            <Switch
              checked={config.showMetadata !== false}
              onChange={handleShowMetadataChange}
              disabled={disabled}
            />
          </Group>

          <Group justify="space-between">
            <div>
              <Text size="sm">Show Files</Text>
              <Text size="xs" c="dimmed">
                List attached files with sizes
              </Text>
            </div>
            <Switch
              checked={config.showFiles !== false}
              onChange={handleShowFilesChange}
              disabled={disabled}
            />
          </Group>

          <Group justify="space-between">
            <div>
              <Text size="sm">Show View Link</Text>
              <Text size="xs" c="dimmed">
                Make title clickable to view submission
              </Text>
            </div>
            <Switch
              checked={config.showViewButton !== false}
              onChange={handleShowViewButtonChange}
              disabled={disabled}
            />
          </Group>
        </Stack>
      </Box>

      {/* Placeholders Help */}
      <Alert icon={<IconInfoCircle size={16} />} color="gray" variant="light">
        <Text size="sm" fw={500} mb={4}>
          Available placeholders for header:
        </Text>
        <Text size="xs" component="div">
          <Code>{"{{formName}}"}</Code> — Form name
          <br />
          <Code>{"{{formId}}"}</Code> — Form UUID
          <br />
          <Code>{"{{requestId}}"}</Code> — Submission ID
          <br />
          <Code>{"{{date}}"}</Code> — Date (YYYY-MM-DD)
        </Text>
      </Alert>

      {/* Info Note */}
      <Alert icon={<IconInfoCircle size={16} />} color="blue" variant="light">
        <Text size="sm">
          Messages are sent as rich embeds with all visible form fields. Hidden
          fields and security fields are automatically excluded.
        </Text>
      </Alert>
    </Stack>
  );
};
